// src/utils/phone.js
// *******************************************************************************
// PHONE NUMBER UTILITIES
// *******************************************************************************
// Utilities for phone number normalization, validation, and formatting
// Supports E.164 format conversion for international phone numbers
// Handles country-specific formatting for BR, US, CA, PT, ES, GB
// Generates complete phone metadata (E.164, national, international, masked, etc.)
// Uses libphonenumber-js for robust international phone number handling
// *******************************************************************************

import {
  parsePhoneNumber,
  isValidPhoneNumber as libIsValidPhoneNumber,
  getCountries,
  getCountryCallingCode,
} from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.full.json';
import countries from 'i18n-iso-countries';
import countriesEN from 'i18n-iso-countries/langs/en.json';
import countriesPT from 'i18n-iso-countries/langs/pt.json';
import countriesES from 'i18n-iso-countries/langs/es.json';
import { returnOnlyNumbers } from './validation.js';
import { throwError } from './error.js';

// *******************************************************************************
// COUNTRY DATA & UTILITIES
// *******************************************************************************

// Register language packs for i18n-iso-countries
countries.registerLocale(countriesEN);
countries.registerLocale(countriesPT);
countries.registerLocale(countriesES);

// Get country flag SVG URL from flag-icons CDN (jsDelivr)
// Provides high-quality 4x3 aspect ratio SVGs for 250+ countries
// Special cases: AC (Ascension Island) → GB (UK flag, as it's a British territory)
// getFlag('BR') -> CDN URL for flag-icons
const getFlag = countryCodeISO2 => {
  if (!countryCodeISO2) return null;
  let cc = String(countryCodeISO2).toLowerCase(); // flag-icons uses lowercase
  if (cc.length !== 2) return null;
  // Map special territories to their governing country's flag
  if (cc === 'ac') cc = 'gb'; // Ascension Island → UK
  // CDN URL: flag-icons v7.0.0, 4x3 aspect ratio (consistent sizing across all countries)
  return `https://cdn.jsdelivr.net/gh/lipis/flag-icons@7.0.0/flags/4x3/${cc}.svg`;
};

// Get list of all supported countries from libphonenumber-js
// Returns array of ISO2 uppercase country codes
const getAvailableCountries = () => {
  return getCountries();
};

// Get country metadata including name and dial code
// getCountryMetadata('BR', 'pt') -> { countryCode, dial, name, names }
const getCountryMetadata = (countryCode, lang = 'en') => {
  try {
    const cc = countryCode.toUpperCase();
    const dialCode = getCountryCallingCode(cc);

    // Get country names in all supported languages using i18n-iso-countries
    const namePT = countries.getName(cc, 'pt') || cc;
    const nameEN = countries.getName(cc, 'en') || cc;
    const nameES = countries.getName(cc, 'es') || cc;

    // Select name in requested language
    const name = lang === 'pt' ? namePT : lang === 'es' ? nameES : nameEN;

    return {
      countryCode: cc,
      dial: dialCode,
      name: name,
      names: {
        pt: namePT,
        en: nameEN,
        es: nameES,
      },
    };
  } catch (e) {
    return null;
  }
};

// Get all countries with their metadata, sorted by name
const getAllCountriesMetadata = (lang = 'en') => {
  const allCodes = getAvailableCountries();
  return allCodes
    .map(cc => getCountryMetadata(cc, lang))
    .filter(Boolean)
    .sort((a, b) => a.name.localeCompare(b.name));
};

// *******************************************************************************
// PROFILE DETECTION & VALIDATION (BRAZIL)
// *******************************************************************************

// Detect phone profile type from digits (Brazil only)
// Returns: 'mobile', 'landline', 'free', 'robot', or null
const detectBrazilianPhoneProfile = digits => {
  if (!digits) return null;
  const d = String(digits);

  // Robot: 3003, 4004 (10 digits total)
  if (d.length === 10 && (d.startsWith('3003') || d.startsWith('4004'))) {
    return 'robot';
  }

  // Free: 0800, 0300 (11 digits total)
  if (d.length === 11 && (d.startsWith('0800') || d.startsWith('0300'))) {
    return 'free';
  }

  // Mobile: 11 digits, starts with 9 after DDD
  if (d.length === 11 && d.charAt(2) === '9') {
    return 'mobile';
  }

  // Landline: 10 digits (any digit after DDD that isn't already matched as robot)
  if (d.length === 10) {
    return 'landline';
  }

  return null;
};

// Validate Brazilian mobile number (strict)
// Rules: 11 digits, DDD 11-99, first digit after DDD = 9, second digit = 6-9
const validateBrazilianMobile = digits => {
  if (!digits || digits.length !== 11) return false;
  const d = String(digits);

  const ddd = parseInt(d.substring(0, 2), 10);
  if (ddd < 11 || ddd > 99) return false;

  if (d.charAt(2) !== '9') return false;

  const secondDigit = parseInt(d.charAt(3), 10);
  return secondDigit >= 6 && secondDigit <= 9;
};

// Validate Brazilian landline number (strict)
// Rules: 10 digits, DDD 11-99, first digit after DDD = 2-5
const validateBrazilianLandline = digits => {
  if (!digits || digits.length !== 10) return false;
  const d = String(digits);

  const ddd = parseInt(d.substring(0, 2), 10);
  if (ddd < 11 || ddd > 99) return false;

  const firstDigit = parseInt(d.charAt(2), 10);
  return firstDigit >= 2 && firstDigit <= 5;
};

// Validate Brazilian free number (0800, 0300)
// Rules: 11 digits, starts with 0800 or 0300
const validateBrazilianFree = digits => {
  if (!digits || digits.length !== 11) return false;
  const d = String(digits);
  return d.startsWith('0800') || d.startsWith('0300');
};

// Validate Brazilian robot number (3003, 4004)
// Rules: 10 digits, starts with 3003 or 4004
const validateBrazilianRobot = digits => {
  if (!digits || digits.length !== 10) return false;
  const d = String(digits);
  return d.startsWith('3003') || d.startsWith('4004');
};

// *******************************************************************************
// PUBLIC METHODS
// *******************************************************************************

// Convert phone number to E.164 format (+country_code + number)
// toE164('11999999999', 'BR') -> '+5511999999999'
const toE164 = (input, defaultCountry = 'BR') => {
  if (input == null) throwError('Invalid phone number');

  // Support objects with fullPhoneNumber property
  if (typeof input === 'object' && input.fullPhoneNumber) {
    const v = String(input.fullPhoneNumber).trim();
    const digits = returnOnlyNumbers(v);
    if (!digits) throwError('Invalid phone number');
    return v.startsWith('+') ? v : `+${digits}`;
  }

  const raw = String(input).trim();

  // Already in E.164 format
  if (raw.startsWith('+')) return raw;

  const digits = returnOnlyNumbers(raw);
  if (!digits) throwError('Invalid phone number');

  const cc = resolveDefaultCountryCode(defaultCountry);

  // Brazil: 10-11 digits
  if (cc === '55' && (digits.length === 10 || digits.length === 11)) {
    return `+55${digits}`;
  }

  // US/Canada: 10-11 digits
  if (cc === '1') {
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  }

  // Default: prepend country code
  return `+${digits}`;
};

// Resolve country calling code from ISO2 country code
// resolveDefaultCountryCode('BR') -> '55'
const resolveDefaultCountryCode = country => {
  const map = {
    BR: '55',
    US: '1',
    CA: '1',
    PT: '351',
    ES: '34',
    GB: '44',
  };
  const key = String(country || '').toUpperCase();
  return map[key] || '55'; // Default to Brazil
};

// Generate complete phone metadata from phone number and country code
// Returns { phone, countryCode, dialCode, fullNumber, whatsappNumber, national, international, masked, areaCode, number, type, isMobile, isLandline, isValid }
const getPhoneMeta = (phone, countryCode = 'BR') => {
  if (!phone) return null;

  try {
    // Handle object input with phone property
    const phoneStr = typeof phone === 'object' ? phone.phone || phone.mobile : phone;
    const country = (
      typeof phone === 'object' ? phone.countryCode || countryCode : countryCode
    )?.toUpperCase();

    if (!phoneStr) return null;

    // Clean phone number (remove special chars but keep +)
    const cleanPhone = String(phoneStr).trim();

    // Try to parse with libphonenumber-js
    let parsed;
    try {
      if (cleanPhone.startsWith('+')) {
        parsed = parsePhoneNumber(cleanPhone);
      } else {
        parsed = parsePhoneNumber(cleanPhone, country);
      }
    } catch (e) {
      // If parsing fails, try to construct E.164 manually
      const digits = returnOnlyNumbers(cleanPhone);
      if (!digits) return null;

      const dialCode = '+' + resolveDefaultCountryCode(country);
      const e164 = cleanPhone.startsWith('+') ? cleanPhone : `${dialCode}${digits}`;

      parsed = parsePhoneNumber(e164);
    }

    if (!parsed || !parsed.isValid()) return null;

    const nationalNumber = parsed.nationalNumber;
    const phoneType = parsed.getType();
    const formattedNational = parsed.format('NATIONAL');

    // Extract area code based on country
    let areaCode = null;
    let justNumber = nationalNumber;

    if (parsed.country === 'BR') {
      areaCode = nationalNumber.substring(0, 2);
      justNumber = nationalNumber.substring(2);
    } else if (['US', 'CA'].includes(parsed.country)) {
      areaCode = nationalNumber.substring(0, 3);
      justNumber = nationalNumber.substring(3);
    }

    // Generate masked version (hide middle digits)
    let masked = formattedNational;
    const digits = formattedNational.replace(/\D/g, '');
    if (digits.length >= 4) {
      const start = formattedNational.substring(0, Math.ceil(formattedNational.length / 3));
      const end = formattedNational.substring(formattedNational.length - 4);
      const middle = '*'.repeat(formattedNational.length - start.length - end.length);
      masked = start + middle + end;
    }

    return {
      phone: returnOnlyNumbers(nationalNumber),
      countryCode: parsed.country,
      dialCode: `+${parsed.countryCallingCode}`,
      fullNumber: parsed.format('E.164'),
      whatsappNumber: parsed.format('E.164').substring(1),
      national: formattedNational,
      international: parsed.format('INTERNATIONAL'),
      masked,
      areaCode,
      number: justNumber,
      type: phoneType,
      isMobile: phoneType === 'MOBILE',
      isLandline: phoneType === 'FIXED_LINE',
      isValid: true,
    };
  } catch (error) {
    return null;
  }
};

// Normalize phone input from frontend to standardized { phone, countryCode }
const normalizePhoneInput = input => {
  if (!input) return null;

  // Already normalized
  if (input.phone && input.countryCode) {
    return {
      phone: returnOnlyNumbers(input.phone),
      countryCode: input.countryCode.toUpperCase(),
    };
  }

  // Legacy format: mobile + dialCode
  if (input.mobile && input.dialCode) {
    const meta = getPhoneMeta(input.mobile, input.dialCode);
    if (!meta) return null;

    return {
      phone: meta.phone,
      countryCode: meta.countryCode,
    };
  }

  // Legacy format: mobile + mobileCountryCode
  if (input.mobile && input.mobileCountryCode) {
    return {
      phone: returnOnlyNumbers(input.mobile),
      countryCode: input.mobileCountryCode.toUpperCase(),
    };
  }

  // Single field with full number
  if (input.mobile || input.phone) {
    const phoneStr = input.mobile || input.phone;
    const cc = input.countryCode || 'BR';
    const meta = getPhoneMeta(phoneStr, cc);
    if (!meta) return null;

    return {
      phone: meta.phone,
      countryCode: meta.countryCode,
    };
  }

  return null;
};

// Fix old Brazilian mobile numbers missing the leading "9" digit
// Common with old data registered before the mandatory "9" prefix (2012-2016)
// fixOldBrazilianMobile('1182320000') -> '11982320000'
const fixOldBrazilianMobile = digits => {
  if (!digits) return digits;

  const d = String(digits).replace(/\D/g, '');

  // Already 11 digits - check if valid mobile
  if (d.length === 11) {
    if (validateBrazilianMobile(d) || validateBrazilianFree(d)) {
      return d;
    }
    return d;
  }

  // Not 10 digits - can't process
  if (d.length !== 10) {
    return d;
  }

  // 10 digits - check if valid landline
  if (validateBrazilianLandline(d)) {
    return d;
  }

  // Check if it's a robot number (3003, 4004)
  if (validateBrazilianRobot(d)) {
    return d;
  }

  // Try adding "9" after DDD to see if it becomes a valid mobile
  const ddd = d.substring(0, 2);
  const number = d.substring(2);

  const firstDigit = parseInt(number.charAt(0), 10);
  if (firstDigit >= 6 && firstDigit <= 9) {
    const potentialMobile = `${ddd}9${number}`;
    if (validateBrazilianMobile(potentialMobile)) {
      return potentialMobile;
    }
  }

  return d;
};

// Validate if a phone number is valid for a given country
// isValidPhoneNumber('19982320000', 'BR') -> true
// isValidPhoneNumber('19982320000', { countryCode: 'BR', profile: 'mobile' }) -> true
const isValidPhoneNumber = (phoneNumber, countryCodeOrOptions = 'BR') => {
  if (!phoneNumber) return false;

  const phoneStr = String(phoneNumber).trim();
  const digits = returnOnlyNumbers(phoneStr);

  if (!digits || digits.length < 7) return false;

  let countryCode = 'BR';
  let profile = null;

  if (typeof countryCodeOrOptions === 'object' && countryCodeOrOptions !== null) {
    countryCode = countryCodeOrOptions.countryCode || 'BR';
    profile = countryCodeOrOptions.profile
      ? String(countryCodeOrOptions.profile).toLowerCase()
      : null;
  } else if (typeof countryCodeOrOptions === 'string') {
    countryCode = countryCodeOrOptions;
  }

  const country = countryCode?.toUpperCase();

  // Profile-based validation for Brazil (strict mode)
  if (country === 'BR' && profile && !phoneStr.startsWith('+')) {
    switch (profile) {
      case 'mobile':
      case 'whatsapp':
      case 'telegram':
        return validateBrazilianMobile(digits);

      case 'landline':
        return validateBrazilianLandline(digits);

      case 'free':
        return validateBrazilianFree(digits);

      case 'robot':
        return validateBrazilianRobot(digits);

      case 'phone': {
        const detectedProfile = detectBrazilianPhoneProfile(digits);
        if (!detectedProfile) return false;
        switch (detectedProfile) {
          case 'mobile': return validateBrazilianMobile(digits);
          case 'landline': return validateBrazilianLandline(digits);
          case 'free': return validateBrazilianFree(digits);
          case 'robot': return validateBrazilianRobot(digits);
          default: return false;
        }
      }

      default:
        return false;
    }
  }

  // Auto-detect mode for Brazilian numbers without profile
  if (country === 'BR' && !profile && !phoneStr.startsWith('+')) {
    const detectedProfile = detectBrazilianPhoneProfile(digits);
    if (!detectedProfile) return false;
    switch (detectedProfile) {
      case 'mobile': return validateBrazilianMobile(digits);
      case 'landline': return validateBrazilianLandline(digits);
      case 'free': return validateBrazilianFree(digits);
      case 'robot': return validateBrazilianRobot(digits);
      default: return false;
    }
  }

  try {
    if (phoneStr.startsWith('+')) {
      return libIsValidPhoneNumber(phoneStr);
    }
    return libIsValidPhoneNumber(phoneStr, country);
  } catch (e) {
    return false;
  }
};

// Format phone number for display
// formatPhoneNumber('19982320000', 'BR', 'national') -> '(19) 98232-0000'
const formatPhoneNumber = (phone, countryCode = 'BR', format = 'national') => {
  const meta = getPhoneMeta(phone, countryCode);
  if (!meta) return null;

  switch (format.toLowerCase()) {
    case 'national': return meta.national;
    case 'international': return meta.international;
    case 'e164': return meta.fullNumber;
    case 'masked': return meta.masked;
    default: return meta.national;
  }
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************

export {
  toE164,
  resolveDefaultCountryCode,
  getPhoneMeta,
  normalizePhoneInput,
  isValidPhoneNumber,
  formatPhoneNumber,
  getAvailableCountries,
  getCountryMetadata,
  getAllCountriesMetadata,
  detectBrazilianPhoneProfile,
  validateBrazilianMobile,
  validateBrazilianLandline,
  validateBrazilianFree,
  validateBrazilianRobot,
  fixOldBrazilianMobile,
  getFlag,
};

export default {
  toE164,
  resolveDefaultCountryCode,
  getPhoneMeta,
  normalizePhoneInput,
  isValidPhoneNumber,
  formatPhoneNumber,
  getAvailableCountries,
  getCountryMetadata,
  getAllCountriesMetadata,
  detectBrazilianPhoneProfile,
  validateBrazilianMobile,
  validateBrazilianLandline,
  validateBrazilianFree,
  validateBrazilianRobot,
  fixOldBrazilianMobile,
  getFlag,
};
